import { useState } from "react";
import axios from "axios";
import { Zap, Plus, Ticket, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { setToken } from "../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OAuthOnboarding({ user, onboardToken, onComplete }) {
  const [mode, setMode] = useState(null); // 'invite' | 'create'
  const [inviteCode, setInviteCode] = useState("");
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [calendly, setCalendly] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (payload) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/oauth/link-family`, payload, {
        headers: { Authorization: `Bearer ${onboardToken}` },
      });
      setToken(res.data.token);
      toast.success(`Sei dentro: ${res.data.family.name}`);
      onComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = () => {
    if (!inviteCode.trim()) return toast.error("Inserisci un codice");
    submit({ invite_code: inviteCode.trim().toUpperCase() });
  };

  const handleCreate = () => {
    if (!name.trim()) return toast.error("Inserisci il nome della Casa");
    if (!whatsapp.trim()) return toast.error("Inserisci il numero WhatsApp");
    if (password && password.length < 4) return toast.error("Password troppo corta");
    submit({
      new_family: {
        name: name.trim(),
        whatsapp_number: whatsapp.trim(),
        calendly_url: calendly.trim(),
        password: password.trim() || undefined,
      },
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FBF9F6] px-4 py-10">
      <div className="pointer-events-none fixed top-[-120px] right-[-120px] h-[420px] w-[420px] rounded-full bg-[#FF7C43]/15 blur-3xl" />
      <div className="pointer-events-none fixed bottom-[-160px] left-[-100px] h-[380px] w-[380px] rounded-full bg-[#C87941]/15 blur-3xl" />

      <div className="relative w-full max-w-md animate-fade">
        {/* User chip */}
        <div className="mb-6 flex items-center gap-3 rounded-full border border-[#E8E1D9] bg-white p-2 pr-4 shadow-sm">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full papaya-gradient text-white font-bold">
              {(user?.name || "?")[0]}
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate font-semibold text-[#2D2926]">{user?.name}</p>
            <p className="truncate text-xs text-[#5C5855]">{user?.email}</p>
          </div>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D2926]">
          A quale Casa entri?
        </h1>
        <p className="mt-2 text-[#5C5855]">
          Scegli se entrare in una Casa esistente con un codice o crearne una nuova.
        </p>

        {!mode && (
          <div className="mt-6 space-y-3">
            <button
              data-testid="onboarding-invite-mode"
              onClick={() => setMode("invite")}
              className="group flex w-full items-center gap-3 rounded-2xl border border-[#E8E1D9] bg-white p-5 text-left transition hover:-translate-y-0.5 hover:border-[#FF7C43] hover:shadow-md"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#FBF9F6] text-[#FF7C43]">
                <Ticket className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <p className="font-display text-lg font-bold text-[#2D2926]">Ho un codice invito</p>
                <p className="text-sm text-[#5C5855]">Inserisci un codice <code>ZAP-XXXXX</code> ricevuto</p>
              </div>
            </button>

            <button
              data-testid="onboarding-create-mode"
              onClick={() => setMode("create")}
              className="group flex w-full items-center gap-3 rounded-2xl border border-[#E8E1D9] bg-white p-5 text-left transition hover:-translate-y-0.5 hover:border-[#FF7C43] hover:shadow-md"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl papaya-gradient text-white">
                <Plus className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <p className="font-display text-lg font-bold text-[#2D2926]">Crea una nuova Casa</p>
                <p className="text-sm text-[#5C5855]">Diventa l'admin di una nuova famiglia</p>
              </div>
            </button>
          </div>
        )}

        {mode === "invite" && (
          <div className="mt-6 space-y-4 rounded-3xl border border-[#E8E1D9] bg-white p-6">
            <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              Codice invito
            </label>
            <Input
              data-testid="onboarding-invite-input"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="ZAP-XXXXX"
              className="rounded-xl border-[#E8E1D9] bg-white text-center font-mono text-lg tracking-[0.2em]"
              autoFocus
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setMode(null)} className="rounded-full border-[#E8E1D9]">
                Indietro
              </Button>
              <Button
                data-testid="onboarding-invite-submit"
                onClick={handleInvite}
                disabled={submitting}
                className="rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]"
              >
                {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                <Zap className="mr-1 h-4 w-4" fill="white" /> Entra
              </Button>
            </div>
          </div>
        )}

        {mode === "create" && (
          <div className="mt-6 space-y-3 rounded-3xl border border-[#E8E1D9] bg-white p-6">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                Nome Casa *
              </label>
              <Input
                data-testid="onboarding-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`Es. Casa di ${(user?.name || "").split(" ")[0] || "Mario"}`}
                className="rounded-xl border-[#E8E1D9] bg-white"
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                WhatsApp *
              </label>
              <Input
                data-testid="onboarding-whatsapp-input"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="393273839782"
                className="rounded-xl border-[#E8E1D9] bg-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                Calendly (opzionale)
              </label>
              <Input
                data-testid="onboarding-calendly-input"
                value={calendly}
                onChange={(e) => setCalendly(e.target.value)}
                placeholder="https://calendly.com/..."
                className="rounded-xl border-[#E8E1D9] bg-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                Codice password (opzionale)
              </label>
              <Input
                data-testid="onboarding-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Lascia vuoto per accesso solo Google"
                className="rounded-xl border-[#E8E1D9] bg-white"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setMode(null)} className="rounded-full border-[#E8E1D9]">
                Indietro
              </Button>
              <Button
                data-testid="onboarding-create-submit"
                onClick={handleCreate}
                disabled={submitting}
                className="rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]"
              >
                {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Crea Casa
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
